import { recordValues } from '@seen/core/utils'
import { cn } from '@seen/ui'
import { IconBrandSlack, IconExternalLink } from '@tabler/icons-react'
import { useMemo } from 'react'
import { IconElation } from '#src/components/icons/IconElation.tsx'
import { IconMedplum } from '#src/components/icons/IconMedplum.tsx'

interface ExternalLinkButtonProps {
  href: string | undefined
  label?: React.ReactNode
  className?: string
  removeIcon?: boolean
}

export function ExternalLink({
  href,
  label,
  className,
  removeIcon,
}: ExternalLinkButtonProps): React.ReactNode {
  const externalService = useMemo(
    () =>
      recordValues(externalURLMap).find(({ matchers }) =>
        matchers.some((matcher) => href && matcher.test(href))
      ),
    [href]
  )

  if (!href) {
    return null
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={cn(
        'relative inline-flex h-fit w-fit items-center gap-1 text-foreground-tertiary text-xs',
        className
      )}
    >
      {label ?? externalService?.label}
      {!removeIcon && <IconExternalLink className="size-4" />}
    </a>
  )
}

interface ExternalService {
  matchers: RegExp[]
  icon?: React.ReactNode
  label: string
}

const externalURLMap = {
  medplum: {
    matchers: [/^https?:\/\/app\.medplum\.com/],
    icon: <IconMedplum size={8} />,
    label: 'Medplum',
  },
  elation: {
    matchers: [
      /^https?:\/\/app\.elationemr\.com/,
      /^https?:\/\/sandbox\.elationemr\.com/,
    ],
    icon: <IconElation className="size-2" />,
    label: 'Elation',
  },
  metriport: {
    matchers: [/^https?:\/\/dash\.metriport\.com/],
    label: 'Metriport',
  },
  slack: {
    matchers: [/slack\.com\//],
    icon: <IconBrandSlack className="size-2" />,
    label: 'Slack',
  },
  langfuse: {
    matchers: [/langfuse/],
    label: 'Trace',
  },
} satisfies Record<string, ExternalService>
